/**
 * @architecture-header 1
 * @layer module
 * @domain reports
 * @responsibility Render the monthly learning report with its trend chart and comment editor.
 * @state-owner dataset store holds prompt settings
 * @allowed-imports React; dataset and workspace stores; shared components, selectors and hooks; module dialogs
 * @forbidden direct API client; student-only surfaces
 * @public-api workspace main column
 * @side-effects navigation, dialogs and toasts
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { FilterBar } from '../../../shared/components/FilterBar';
import { useOverlay } from '../../../shared/components/OverlayHost';
import { Page, PageBody, PageHeader } from '../../../shared/components/PageShell';
import { RUN_MODE_LABEL, RUN_STATUS_LABEL } from '../../../shared/lib/demo-data';
import { filterDefinitions } from '../../../shared/lib/filters';
import { myClassIndexes, runName } from '../../../shared/lib/selectors';
import { useDatasetStore } from '../../../shared/model/dataset-store';
import { useTranslator } from '../../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';
import { buildReportComment, resolvePrompt } from '../model/comment';
import { PromptDialog } from './PromptDialog';

const TREND = [52, 58, 61, 70, 76, 84];

function TrendChart({ values }: { values: readonly number[] }) {
  const width = 460;
  const height = 110;
  const max = Math.max(...values);
  const min = Math.min(...values);
  const points = values.map((value, index) => [
    16 + index * ((width - 32) / (values.length - 1)),
    height - 14 - ((value - min) / Math.max(1, max - min)) * (height - 40),
  ]);
  return (
    <svg aria-hidden="true" style={{ width: '100%', height }} viewBox={`0 0 ${width} ${height}`}>
      <title>trend</title>
      <polyline
        fill="none"
        points={points.map((point) => point.join(',')).join(' ')}
        stroke="var(--primary)"
        strokeLinejoin="round"
        strokeWidth="2.5"
      />
      {points.map((point, index) => (
        <circle
          cx={point[0]}
          cy={point[1]}
          fill={index === points.length - 1 ? 'var(--primary)' : '#fff'}
          key={`${point[0]}-${point[1]}`}
          r={index === points.length - 1 ? 5 : 3.5}
          stroke="var(--primary)"
          strokeWidth="2"
        />
      ))}
    </svg>
  );
}

export function LearningReport({ student }: { student: string }) {
  const t = useTranslator();
  const { openModal } = useOverlay();
  const classes = useDatasetStore((state) => state.classes);
  const activities = useDatasetStore((state) => state.activities);
  const runs = useDatasetStore((state) => state.runs);
  const prompts = useDatasetStore((state) => state.prompts.rep);
  const notify = useWorkspaceUiStore((state) => state.notify);
  const navigate = useWorkspaceUiStore((state) => state.navigate);
  const openItem = useWorkspaceUiStore((state) => state.openItem);

  const membership = classes.filter((item) => item.students.includes(student));
  const comment = buildReportComment(student, resolvePrompt('rep', prompts), t);

  return (
    <Page maxWidth={920}>
      <PageHeader
        actions={
          <>
            <button className="bt" type="button" onClick={() => notify(t('已切換到近 90 天'))}>
              {t('近 30 天')}
            </button>
            <button className="bt pri" type="button" onClick={() => notify(t('已發送給家長'))}>
              {t('發送')}
            </button>
          </>
        }
        meta={membership.map((item) => item.name).join('、')}
        title={student}
      />
      <div className="vtbar">
        <FilterBar
          definitions={filterDefinitions('rep', classes, myClassIndexes(classes))}
          scope="rep"
        />
      </div>
      <PageBody>
        <section className="panel" style={{ marginTop: 18 }}>
          <header>
            <h2>{t('自己答對的比率')}</h2>
            <span className="n">{t('不含提示後才對的')}</span>
          </header>
          <div className="chartbox">
            <TrendChart values={TREND} />
            <div className="xlab">
              <span>6 週前</span>
              <span>5 週前</span>
              <span>4 週前</span>
              <span>3 週前</span>
              <span>2 週前</span>
              <span>{t('本週')}</span>
            </div>
          </div>
          <p
            style={{
              fontSize: 16,
              lineHeight: '27px',
              color: 'var(--on-surface-variant)',
              margin: '14px 2px 0',
            }}
          >
            {t('從')} <b>{`${TREND[0]}%`}</b> {t('上升到')} <b>{`${TREND[TREND.length - 1]}%`}</b>,{' '}
            {t('六週進步')} <b>{TREND[TREND.length - 1] - TREND[0]}</b> {t('個百分點')}
          </p>
        </section>

        <div className="dgrid" style={{ marginBottom: 34 }}>
          <div className="dcard">
            <h6>{t('進步最多')}</h6>
            <b style={{ fontSize: 19, fontWeight: 700, display: 'block', lineHeight: '28px' }}>
              {t('列式')}
            </b>
            <span>62% → 88%</span>
          </div>
          <div className="dcard">
            <h6>{t('最需要練')}</h6>
            <b
              style={{
                fontSize: 19,
                fontWeight: 700,
                display: 'block',
                lineHeight: '28px',
                color: 'var(--error)',
              }}
            >
              {t('移項變號')}
            </b>
            <span>{t('47% · 近三週錯 5 次')}</span>
          </div>
          <div className="dcard">
            <h6>{t('證據')}</h6>
            <b className="big">18</b>
            <span>{`${t('題完成')} · 11 ${t('題沒用提示')} · 3 ${t('次老師介入')}`}</span>
          </div>
        </div>

        <section className="panel">
          <header>
            <h2>{t('評語')}</h2>
            <div className="sp" />
            <button
              className="lk"
              type="button"
              onClick={() => openModal(<PromptDialog kind="rep" />)}
            >
              {t('提示詞')}
            </button>
            <button className="lk" type="button" onClick={() => notify(t('已重新生成'))}>
              {t('重新生成')}
            </button>
          </header>
          <div className="ed3">
            <textarea
              defaultValue={comment}
              key={`${student}-${comment}`}
              style={{ minHeight: 110 }}
            />
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="bt" type="button" onClick={() => notify(t('已儲存'))}>
              {t('儲存')}
            </button>
            <button className="bt pri" type="button" onClick={() => notify(t('已核准，可以發送'))}>
              {t('核准')}
            </button>
          </div>
        </section>

        <section className="panel flush">
          <header>
            <h2>{t('這段時間的活動')}</h2>
          </header>
          <div>
            {runs
              .filter((run) => classes[run.classIndex].students.includes(student))
              .map((run) => (
                <button
                  className="it"
                  key={run.id}
                  type="button"
                  onClick={() => {
                    navigate('room', `c${run.classIndex}`);
                    openItem({ k: 'rev', id: run.id });
                  }}
                >
                  <div className="av">{t('題')}</div>
                  <div className="t">
                    <b>{runName(activities, run)}</b>
                    <span>
                      {`${t(run.dateLabel)} · ${classes[run.classIndex].name} · ${t(RUN_MODE_LABEL[run.mode])}`}
                    </span>
                  </div>
                  <em>{run.status === 'ended' ? '88 分' : t(RUN_STATUS_LABEL[run.status])}</em>
                </button>
              ))}
          </div>
        </section>
      </PageBody>
    </Page>
  );
}
